import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
    List, ListItem, ListItemText, ListItemAvatar, Avatar, ToggleButtonGroup,
    ToggleButton, Box, Typography, Button, Grid, Paper,
    InputAdornment, CircularProgress, Chip, Rating, IconButton
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import EmailIcon from '@mui/icons-material/Email';
import CategoryIcon from '@mui/icons-material/Category';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { FixedSizeList as VirtualList } from 'react-window';
import { getCardColor, getTextColor } from "../../utils/colorUtils";
import { useGetSimulationsQuery } from "../../store/services/simulation-service";
import MainCard from "../../components/cards/MainCard";
import { CalendarSimulationPreviewModal } from "../delivery/components/modals/CalendarSimulationPreview";
import { ModalProvider } from "../../components/Modal/ModalContext";
import TrimmedTextField from "../../components/TrimmedTextField";

const EmptyStateContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(3),
    textAlign: 'center',
}));

const EmptyStateIcon = styled(Box)(({ theme }) => ({
    color: theme.palette.action.disabled,
    marginBottom: theme.spacing(2),
    '& svg': {
        fontSize: 64,
    },
}));

const DroppableSection = styled(Paper)(({ theme, isDragOver }) => ({
    height: 700,
    minHeight: 700,
    padding: theme.spacing(2),
    backgroundColor: isDragOver ? getCardColor(theme.palette.mode === 'light') : getCardColor(theme.palette.mode === 'light', false),
    border: `1px solid ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`,
    borderRadius: theme.borderRadius.sm,
    boxShadow: theme.customShadows.z1,
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const ContentArea = styled(Box)({
    flexGrow: 1,
    overflow: 'hidden',
    position: 'relative',
    minHeight: 0,
});

const SearchContainer = styled(Box)({
    flexGrow: 1,
});

const SimulationListItem = styled(ListItem)(({ theme, isDragging }) => ({
    backgroundColor: getCardColor(theme.palette.mode === 'light', false),
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: theme.borderRadius.sm,
    marginBottom: theme.spacing(1),
    opacity: isDragging ? 0.5 : 1,
    cursor: 'move',
    padding: theme.spacing(1, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover': {
        backgroundColor: getCardColor(theme.palette.mode === 'light'),
    },
    '& img': {
        pointerEvents: 'none',
    },
}));

const SimulationCard = styled(MainCard)(({ theme, isDragging }) => ({
    backgroundColor: getCardColor(theme.palette.mode === 'light', false),
    opacity: isDragging ? 0.5 : 1,
    cursor: 'move',
    transition: 'all 0.2s ease',
    transform: isDragging ? 'scale(1.02)' : 'scale(1)',
    position: 'relative',
    '&:hover': {
        transform: isDragging ? 'scale(1.02)' : 'scale(1.01)',
        boxShadow: theme.shadows[4],
        '& .preview-button': {
            opacity: 1,
        },
    },
}));

const ListItemContent = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
});

const PreviewButton = styled(Button)(({ theme }) => ({
    marginLeft: theme.spacing(2),
    minWidth: '100px',
}));

// Optimized drag preview that uses CSS transforms for positioning
const DragPreview = ({ show, x, y, simulation, theme }) => {
    const previewRef = useRef(null);

    useEffect(() => {
        if (previewRef.current && show) {
            previewRef.current.style.transform = `translate3d(${x - 150}px, ${y - 30}px, 0)`;
        }
    }, [x, y, show]);

    if (!show || !simulation) return null;

    return (
        <Box
            ref={previewRef}
            sx={{
                position: 'fixed',
                left: 0,
                top: 0,
                transform: 'translate3d(-150px, -30px, 0)',
                opacity: 0.8,
                pointerEvents: 'none',
                zIndex: theme.zIndex.tooltip + 1,
                willChange: 'transform',
                width: 300,
            }}
        >
            <SimulationCard content={true} boxShadow={true} border={false}>
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {simulation.simulationName}
                    </Typography>
                    <Chip
                        icon={<CategoryIcon />}
                        label={simulation.simulationType}
                        size="small"
                        variant="outlined"
                    />
                </Box>
            </SimulationCard>
        </Box>
    );
};

// Compact list item component - similar to course list view
const VirtualizedListItem = React.memo(({
    index,
    style,
    data: { simulations, draggedSimulation, onDragStart, onDrag, onDragEnd, onPreview, theme }
}) => {
    const simulation = simulations[index];
    const isDragging = draggedSimulation?.simulation.id === simulation.id;

    return (
        <div style={style}>
            <SimulationListItem
                draggable="true"
                onDragStart={(e) => onDragStart(e, simulation)}
                onDrag={onDrag}
                onDragEnd={onDragEnd}
                isDragging={isDragging}
                sx={{ margin: '4px 0' }}
            >
                <ListItemContent>
                    <ListItemText
                        sx={{ ml: 2 }}
                        primary={simulation.simulationName}
                        secondary={
                            <Chip
                                icon={<CategoryIcon />}
                                label={simulation.simulationType}
                                size="small"
                                variant="outlined"
                                sx={{ mt: 0.5 }}
                            />
                        }
                    />
                </ListItemContent>
                <PreviewButton
                    variant="contained"
                    color={theme.palette.mode === 'light' ? "warning" : "primary"}
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onPreview(simulation);
                    }}
                    sx={{
                        ...(theme.palette.mode !== 'light' && {
                            bgcolor: theme.palette.primary.main,
                            color: getTextColor(theme.palette.primary.main),
                            '&:hover': {
                                bgcolor: "#69686b",
                            },
                        }),
                    }}
                >
                    Preview
                </PreviewButton>
            </SimulationListItem>
        </div>
    );
});

// Detailed grid item component with full information
const VirtualizedGridItem = React.memo(({
    index,
    style,
    data: { simulations, draggedSimulation, onDragStart, onDrag, onDragEnd, onPreview, theme }
}) => {
    const simulation = simulations[index];
    const isDragging = draggedSimulation?.simulation.id === simulation.id;

    const handlePreviewClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onPreview(simulation);
    };

    return (
        <div style={{ ...style, padding: '8px' }}>
            <Box
                draggable
                onDragStart={(e) => onDragStart(e, simulation)}
                onDrag={onDrag}
                onDragEnd={onDragEnd}
                sx={{
                    opacity: isDragging ? 0.5 : 1,
                    cursor: 'move'
                }}
            >
                <SimulationCard
                    content={true}
                    boxShadow={true}
                    border={false}
                    isDragging={isDragging}
                >
                    <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="h6">
                                {simulation.simulationName}
                            </Typography>
                            <Chip
                                icon={<CategoryIcon />}
                                label={simulation.simulationType}
                                size="small"
                                variant="outlined"
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="textSecondary">
                                {simulation.emailFromAddress}
                            </Typography>
                        </Box>

                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            Subject: {simulation.emailSubject || "No subject"}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="textSecondary">
                                Difficulty:
                            </Typography>
                            <Rating
                                value={simulation.difficultyRating}
                                max={3}
                                readOnly
                                size="small"
                                icon={<StarRoundedIcon fontSize="inherit" />}
                                emptyIcon={<StarRoundedIcon fontSize="inherit" />}
                            />
                            <Button sx={{ marginLeft: 'auto' }} onClick={handlePreviewClick} size="small" variant="contained" color="warning">
                                Preview
                            </Button>
                        </Box>
                    </Box>
                </SimulationCard>
            </Box>
        </div>
    );
});

const SimulationSection = React.memo(({
    title,
    simulations,
    onDrop,
    sectionId,
    draggedSimulation,
    setDraggedSimulation,
    isDragOver,
    setIsDragOver,
    showSearch = false,
    count,
    onPreview
}) => {
    const theme = useTheme();
    const [viewMode, setViewMode] = useState(sectionId === 'available' ? 'list' : 'grid');
    const [searchQuery, setSearchQuery] = useState('');
    const [dragPreview, setDragPreview] = useState({ show: false, x: 0, y: 0, simulation: null });

    // New state and ref for dynamic height measurement
    const [containerHeight, setContainerHeight] = useState(500);
    const contentAreaRef = useRef(null);

    // Use RAF for smoother drag tracking
    const rafRef = useRef();
    const isDraggingRef = useRef(false);

    // Dynamic height measurement
    useEffect(() => {
        const measureHeight = () => {
            if (contentAreaRef.current) {
                const rect = contentAreaRef.current.getBoundingClientRect();
                setContainerHeight(rect.height);
            }
        };

        // Initial measurement
        measureHeight();

        // Add resize listener
        window.addEventListener('resize', measureHeight);

        // Use ResizeObserver if available for more accurate measurements
        let resizeObserver;
        if (window.ResizeObserver && contentAreaRef.current) {
            resizeObserver = new ResizeObserver(measureHeight);
            resizeObserver.observe(contentAreaRef.current);
        }

        return () => {
            window.removeEventListener('resize', measureHeight);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, []);

    const filteredSimulations = useMemo(() =>
        simulations.filter(simulation =>
            simulation.simulationName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            simulation.emailSubject.toLowerCase().includes(searchQuery.toLowerCase()) ||
            simulation.simulationType.toLowerCase().includes(searchQuery.toLowerCase())
        ), [simulations, searchQuery]
    );

    const handleDragStart = useCallback((e, simulation) => {
        e.stopPropagation();

        // Create invisible drag image
        const dragImage = document.createElement('div');
        dragImage.style.opacity = '0';
        document.body.appendChild(dragImage);
        e.dataTransfer.setDragImage(dragImage, 0, 0);
        setTimeout(() => document.body.removeChild(dragImage), 0);

        isDraggingRef.current = true;
        setDraggedSimulation({ simulation, sectionId, sourceViewMode: viewMode });
        setDragPreview({ show: true, x: e.clientX, y: e.clientY, simulation });
    }, [sectionId, viewMode, setDraggedSimulation]);

    const handleDrag = useCallback((e) => {
        if (e.clientX === 0 && e.clientY === 0 || !isDraggingRef.current) return;

        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
            if (isDraggingRef.current) {
                setDragPreview(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
            }
        });
    }, []);

    const handleDragEnd = useCallback(() => {
        isDraggingRef.current = false;
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
        }
        setDragPreview({ show: false, x: 0, y: 0, simulation: null });
        setDraggedSimulation(null);
        setIsDragOver(null);
    }, [setDraggedSimulation, setIsDragOver]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        if (!isDragOver) setIsDragOver(sectionId);
    }, [isDragOver, sectionId, setIsDragOver]);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setIsDragOver(null);
    }, [setIsDragOver]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(null);
        if (draggedSimulation && draggedSimulation.sectionId !== sectionId) {
            onDrop(draggedSimulation.simulation.id, draggedSimulation.sectionId, sectionId);
        }
        setDraggedSimulation(null);
        setDragPreview({ show: false, x: 0, y: 0, simulation: null });
    }, [draggedSimulation, sectionId, onDrop, setDraggedSimulation, setIsDragOver]);

    const renderEmptyState = () => {
        if (searchQuery) {
            return (
                <EmptyStateContainer>
                    <EmptyStateIcon><SearchOffIcon /></EmptyStateIcon>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                        No Matching Simulations Found
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        We couldn't find any simulations matching "{searchQuery}"
                    </Typography>
                    <Button variant="text" onClick={() => setSearchQuery('')} sx={{ mt: 2 }}>
                        Clear Search
                    </Button>
                </EmptyStateContainer>
            );
        }

        return (
            <EmptyStateContainer>
                <EmptyStateIcon><FormatListBulletedIcon /></EmptyStateIcon>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                    {sectionId === 'available' ? 'No Available Simulations' : 'No Selected Simulations'}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    {sectionId === 'available'
                        ? 'All simulations have been added to the collection.'
                        : 'Start building your collection by dragging simulations from the available section.'}
                </Typography>
            </EmptyStateContainer>
        );
    };

    const itemData = useMemo(() => ({
        simulations: filteredSimulations,
        draggedSimulation,
        onDragStart: handleDragStart,
        onDrag: handleDrag,
        onDragEnd: handleDragEnd,
        onPreview,
        theme
    }), [filteredSimulations, draggedSimulation, handleDragStart, handleDrag, handleDragEnd, onPreview, theme]);

    const renderVirtualizedContent = () => {
        if (filteredSimulations.length === 0) {
            return renderEmptyState();
        }

        const itemHeight = viewMode === 'grid' ? 230 : 90;
        // Use the measured container height with a small buffer for safety
        const safeHeight = Math.max(containerHeight - 10, 100); // Minimum 100px height
        const height = Math.min(safeHeight, filteredSimulations.length * itemHeight);

        return (
            <VirtualList
                height={height}
                itemCount={filteredSimulations.length}
                itemSize={itemHeight}
                itemData={itemData}
                overscanCount={5}
            >
                {viewMode === 'grid' ? VirtualizedGridItem : VirtualizedListItem}
            </VirtualList>
        );
    };

    return (
        <>
            <DroppableSection
                className="content-area"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                isDragOver={isDragOver === sectionId}
            >
                <Typography variant="h4" sx={{ pb: 2 }}>
                    {title} ({count})
                </Typography>

                <ControlsContainer>
                    {showSearch ? (
                        <SearchContainer>
                            <TrimmedTextField
                                fullWidth
                                size="small"
                                placeholder="Search simulations..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </SearchContainer>
                    ) : (
                        <Box flexGrow={1} />
                    )}
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newView) => newView && setViewMode(newView)}
                        size="small"
                    >
                        <ToggleButton value="grid"><GridViewIcon /></ToggleButton>
                        <ToggleButton value="list"><ListIcon /></ToggleButton>
                    </ToggleButtonGroup>
                </ControlsContainer>

                <ContentArea ref={contentAreaRef}>
                    {renderVirtualizedContent()}
                </ContentArea>
            </DroppableSection>

            {dragPreview.show && draggedSimulation?.sectionId === sectionId && (
                <DragPreview
                    show={dragPreview.show}
                    x={dragPreview.x}
                    y={dragPreview.y}
                    simulation={dragPreview.simulation}
                    theme={theme}
                />
            )}
        </>
    );
});

/**
 * Wrapper component with ModalProvider
 */
const SimulationCollectionFormWithProvider = (props) => {
    return (
        <ModalProvider>
            <SimulationCollectionForm {...props} />
        </ModalProvider>
    );
};

const SimulationCollectionForm = ({ initialData, onSave }) => {
    const [selectedSimulations, setSelectedSimulations] = useState(initialData?.simulationIds || []);
    const [draggedSimulation, setDraggedSimulation] = useState(null);
    const [isDragOver, setIsDragOver] = useState(null);

    // Preview modal state
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [selectedSimulationId, setSelectedSimulationId] = useState(null);

    const { tenancyId } = useSelector((state) => state.tenancy);

    const user = JSON.parse(localStorage.getItem('user'));
    const additionalQueryParams = user?.role === "Administrators"
        ? ""
        : `&published=true&tenancyId=${tenancyId}`;

    useEffect(() => {
        if (initialData?.simulationIds) {
            setSelectedSimulations(initialData.simulationIds);
        }
    }, [initialData?.simulationIds]);



    const { data: allSimulations = [], isLoading } = useGetSimulationsQuery({
        perPage: 1000,
        page: 0,
        includes: '',
        query: '',
        additionalQueryParams: additionalQueryParams
    });

    const availableSimulations = useMemo(() =>
        allSimulations.filter(simulation => !selectedSimulations.includes(simulation.id)),
        [allSimulations, selectedSimulations]
    );

    const selectedSimulationObjects = useMemo(() =>
        allSimulations.filter(simulation => selectedSimulations.includes(simulation.id)),
        [allSimulations, selectedSimulations]
    );

    const handleDrop = useCallback((simulationId, sourceType, targetType) => {
        if (sourceType === targetType) return;

        const newSelectedSimulations = targetType === 'selected'
            ? [...selectedSimulations, simulationId]
            : selectedSimulations.filter(id => id !== simulationId);

        setSelectedSimulations(newSelectedSimulations);
        onSave({ simulationIds: newSelectedSimulations });
    }, [selectedSimulations, onSave]);

    const handlePreview = useCallback((simulation) => {
        setSelectedSimulationId(simulation.id);
        setPreviewModalOpen(true);
    }, []);

    const handleClosePreview = useCallback(() => {
        setPreviewModalOpen(false);
        setSelectedSimulationId(null);
    }, []);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading simulations...</Typography>
            </Box>
        );
    }

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <SimulationSection
                        title="Available Simulations"
                        simulations={availableSimulations}
                        onDrop={handleDrop}
                        sectionId="available"
                        draggedSimulation={draggedSimulation}
                        setDraggedSimulation={setDraggedSimulation}
                        isDragOver={isDragOver}
                        setIsDragOver={setIsDragOver}
                        showSearch={true}
                        count={availableSimulations.length}
                        onPreview={handlePreview}
                    />
                </Grid>
                <Grid item xs={6}>
                    <SimulationSection
                        title="Collection Simulations"
                        simulations={selectedSimulationObjects}
                        onDrop={handleDrop}
                        sectionId="selected"
                        draggedSimulation={draggedSimulation}
                        setDraggedSimulation={setDraggedSimulation}
                        isDragOver={isDragOver}
                        setIsDragOver={setIsDragOver}
                        showSearch={false}
                        count={selectedSimulations.length}
                        onPreview={handlePreview}
                    />
                </Grid>
            </Grid>

            {/* Preview Modal */}
            <CalendarSimulationPreviewModal
                simulationId={selectedSimulationId || ''}
                open={previewModalOpen && !!selectedSimulationId}
                onClose={handleClosePreview}
                isStandalone={true}
            />
        </>
    );
};

SimulationCollectionForm.propTypes = {
    initialData: PropTypes.object.isRequired,
    onSave: PropTypes.func.isRequired,
    mode: PropTypes.string.isRequired,
    isSaving: PropTypes.bool,
};

export default SimulationCollectionFormWithProvider;